import type { ReactNode } from 'react'
import { getClubBanner } from '../../clubBanners'
import { teamDisplayName } from '../../teams'
import QuestEmptyShield from '../QuestEmptyShield'
import { SHIELD_SIZE, type MatchQuestPick, type OverUnderQuestPick, type QuestPhase, type QuestPick, type TeamQuestPick } from './questTypes'

// Wraps a shield in its own tap target when a change handler is supplied —
// once a pick is made, the shield itself (not text below it) is what reopens
// the picker to change it; while unpicked, the whole card is already the tap
// target (see QuestCard's isCardTappable) so no handler is passed.
export function ShieldButton({
  shield,
  onShieldClick,
}: {
  shield: ReactNode
  onShieldClick?: () => void
}) {
  if (!onShieldClick) return <>{shield}</>
  return (
    <button
      type="button"
      className="quest-card__shield-btn"
      onClick={(event) => {
        event.stopPropagation()
        onShieldClick()
      }}
    >
      {shield}
    </button>
  )
}

export function TeamPick({
  phase,
  pick,
  onShieldClick,
}: {
  phase: QuestPhase
  pick: TeamQuestPick
  onShieldClick?: () => void
}) {
  const banner = pick.team ? getClubBanner(pick.team) : undefined
  const shield = banner ? (
    <img className="quest-card__shield" src={banner} alt="" aria-hidden="true" />
  ) : (
    <QuestEmptyShield size={SHIELD_SIZE} className="quest-card__shield" />
  )
  // Editable-with-a-pick and sealed-with-a-pick both drop the
  // team-name label entirely — the crest already carries that information,
  // per the epic's shared "no team-name text label under a crest, in any
  // state" rule. The unpicked cta caption ("Tap to pick your team") stays,
  // since that isn't a name label.
  const showLabel = !pick.team || (phase !== 'confirm' && phase !== 'locked')
  return (
    <div className="quest-card__team-pick">
      <ShieldButton shield={shield} onShieldClick={onShieldClick} />
      {showLabel && (
        <span
          className={`quest-card__pick-label${
            !pick.team
              ? ' quest-card__pick-label--cta'
              : phase === 'confirm'
                ? ' quest-card__pick-label--confirm'
                : ''
          }`}
        >
          {pick.team ? teamDisplayName(pick.team) : 'Tap to pick your team'}
        </span>
      )}
    </div>
  )
}

export function MatchPick({
  pick,
  onShieldClick,
}: {
  pick: MatchQuestPick
  onShieldClick?: () => void
}) {
  const game = pick.game
  const homeBanner = game ? getClubBanner(game.homeTeam) : undefined
  const awayBanner = game ? getClubBanner(game.awayTeam) : undefined
  const homeShield = homeBanner ? (
    <img className="quest-card__shield" src={homeBanner} alt="" aria-hidden="true" />
  ) : (
    <QuestEmptyShield size={SHIELD_SIZE} className="quest-card__shield" />
  )
  const awayShield = awayBanner ? (
    <img className="quest-card__shield" src={awayBanner} alt="" aria-hidden="true" />
  ) : (
    <QuestEmptyShield size={SHIELD_SIZE} className="quest-card__shield" />
  )
  return (
    <div className="quest-card__match-pick">
      <div className="quest-card__match-shields">
        <ShieldButton shield={homeShield} onShieldClick={onShieldClick} />
        <span className="quest-card__match-divider" aria-hidden="true">
          &ndash;
        </span>
        <ShieldButton shield={awayShield} onShieldClick={onShieldClick} />
      </div>
      {!game && (
        <span className="quest-card__pick-label quest-card__pick-label--cta">
          Tap to pick a match
        </span>
      )}
    </div>
  )
}

export function OverUnderPick({
  phase,
  pick,
  onSelectSide,
  disabled,
}: {
  phase: QuestPhase
  pick: OverUnderQuestPick
  onSelectSide?: (side: 'over' | 'under') => void
  disabled?: boolean
}) {
  // Dimmed pre-start reuses the locked (non-interactive) choice display —
  // there's no pick to make yet, since the whole card is inert until unlock.
  const isLocked = phase === 'locked' || phase === 'dimmed'
  const hasPick = pick.side !== undefined
  return (
    <div className={`quest-card__ou${hasPick ? ' quest-card__ou--picked' : ''}`}>
      <div className="quest-card__ou-read">
        <span className="quest-card__ou-line">{pick.line}</span>
      </div>
      {isLocked ? (
        <div className="quest-card__ou-choice">
          {/* Sealed-with-a-pick: the unselected side doesn't just
              demote, it's gone from the DOM — only the picked side remains,
              read as a plain statement rather than a chosen-among-two
              control. Dimmed (pre-start, no pick yet) still shows both sides
              as inert placeholders, same as before. */}
          {phase === 'locked' && hasPick ? (
            <span className="quest-card__ou-side quest-card__ou-side--active">
              {pick.side === 'under' ? 'UNDER' : 'OVER'}
            </span>
          ) : (
            <>
              <span
                className={`quest-card__ou-side${
                  pick.side === 'under' ? ' quest-card__ou-side--active' : ' quest-card__ou-side--inactive'
                }`}
              >
                UNDER
              </span>
              <span
                className={`quest-card__ou-side${
                  pick.side === 'over' ? ' quest-card__ou-side--active' : ' quest-card__ou-side--inactive'
                }`}
              >
                OVER
              </span>
            </>
          )}
        </div>
      ) : (
        <div className="quest-card__ou-toggle-wrap">
          <div className="quest-card__ou-toggle">
            <button
              type="button"
              disabled={disabled}
              className={`quest-card__ou-toggle-btn${
                pick.side === 'under'
                  ? ' quest-card__ou-toggle-btn--active'
                  // Once a pick exists, the unselected side is pushed well
                  // back — only once a choice has actually
                  // been made; the initial cta toggle (no pick yet) stays
                  // symmetric.
                  : hasPick
                    ? ' quest-card__ou-toggle-btn--receded'
                    : ''
              }`}
              onClick={(event) => {
                event.stopPropagation()
                onSelectSide?.('under')
              }}
            >
              UNDER
            </button>
            <button
              type="button"
              disabled={disabled}
              className={`quest-card__ou-toggle-btn${
                pick.side === 'over'
                  ? ' quest-card__ou-toggle-btn--active'
                  : hasPick
                    ? ' quest-card__ou-toggle-btn--receded'
                    : ''
              }`}
              onClick={(event) => {
                event.stopPropagation()
                onSelectSide?.('over')
              }}
            >
              OVER
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export function QuestPickSlot({
  phase,
  pick,
  onSelectSide,
  onShieldClick,
  deadlinePassed,
}: {
  phase: QuestPhase
  pick: QuestPick
  onSelectSide?: (side: 'over' | 'under') => void
  onShieldClick?: () => void
  deadlinePassed?: boolean
}) {
  if (pick.type === 'team') return <TeamPick phase={phase} pick={pick} onShieldClick={onShieldClick} />
  if (pick.type === 'match') return <MatchPick pick={pick} onShieldClick={onShieldClick} />
  return (
    <OverUnderPick phase={phase} pick={pick} onSelectSide={onSelectSide} disabled={deadlinePassed} />
  )
}
